import {
    useCallback, useEffect, useMemo, useState
} from 'react';
import framework from 'utils/framework';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import SignInEN from 'pages/Auth/locales/SignIn_en_US';
import SignInFR from 'pages/Auth/locales/SignIn_fr_CA';
import { colors, fontSizes } from 'style/config';
import Link from 'components/Link/Link.f';
import { MFA_MODE } from 'constants/phoneVerification';
import OtpEntryScreen from 'pages/Auth/SignIn/OtpEntryScreen.f';
import useOtpSession from 'pages/Auth/SignIn/hooks/useOtpSession';

const { wrapFunctionalComponent } = framework;
const getText = LanguageLocaleUtils.getLocalisedDataFromResource(SignInEN, SignInFR);

const noop = () => {};

// The ONE target the forced-challenge OTP wrapper is allowed to propagate to the
// shell: back to the method chooser (AuthorizationScreen, 'authorization'), where
// the customer re-picks SMS/Email/password. This is a screen SignIn.f.jsx actually
// renders (routing to a not-yet-rendered SIM-9 'risk-step-up-channel-swap' screen
// would blank the slot). Returning to the chooser is still BR-041-safe: the forced
// challenge is re-derived on the next selection; it is NOT a password bypass.
// Any OTHER target coming from inside the wrapped OtpEntryScreen ('credential',
// 'password', etc.) is a BR-041 bypass attempt and MUST NOT reach the parent goTo.
const CHANNEL_SWAP_TARGET = 'authorization';
// OtpEntryScreen's own Try-Another-Method hardcodes goTo('authorization') (per
// SIM-7's implementation at OtpEntryScreen.f.jsx:250-252). The wrapper allows that
// single target through to the chooser and blocks every other bypass path — keeping
// SIM-8 fully decoupled from the SIM-7 file.
const OTP_TRY_ANOTHER_TARGET = 'authorization';

/**
 * RiskStepUpOtpScreen (SIM-8 / ACS-4132, SUB-TASK-2).
 *
 * Forced-challenge OTP step-up wrapper: mounted when the password response returns
 * mfaEnabled (login.js:206-210). Wraps the SIM-7 OtpEntryScreen for all
 * send/verify/masked-destination/resend/dark-scrim behaviour and enforces BR-041 —
 * "Try Another Method" here can ONLY switch OTP channel (SMS ↔ Email); it can
 * NEVER revert to password. The wrapper is a thin adapter, not a re-implementation
 * of the OTP UI: it owns the useOtpSession lifetime (cooldown carry — BR-025) and
 * intercepts navigation to enforce the forced-challenge whitelist.
 *
 * Session lifetime + cooldown carry (P2-T7 / BR-025): the wrapper owns ONE
 * useOtpSession instance for its lifetime, and fires the initial /validateUser
 * send itself (once, guarded by a ref) — passing skipInitialSend={true} to
 * OtpEntryScreen so the POST fires EXACTLY ONCE per wrapper lifetime. Awaiting
 * the send inside the async fire handler lets the wrapper set a `sendSettled`
 * flag when the setter has landed; the child mount happens WITH the settled
 * session, so the masked destination is populated on OtpEntryScreen's first
 * render without any need for a display fallback that would short-circuit the
 * async chain in test assertions.
 *
 * Method starts from authContext.mfaMode (phone|email) — the SAME MFA_MODE enum
 * the shared login flow writes (constants/phoneVerification.js). The passthrough
 * method matches the canonical OTP method values consumed by OtpEntryScreen and
 * useOtpSession ('phone' | 'email').
 *
 * Alt-channel swap (Figma 2271-60328): on a PHONE step-up the wrapper passes an
 * `altChannelAffordance` render slot into OtpEntryScreen — "No Access to this phone?
 * Send verification code via email" (a design-system Link, blue). Clicking it fires
 * an email OTP send on the SAME session (cooldown/counters carry — BR-025) and flips
 * a LOCAL activeMode to email, re-rendering OtpEntryScreen in email mode with the
 * masked-email destination — WITHOUT re-navigating the parent flow (no bypass; the
 * BR-041 whitelist is untouched). The affordance is phone-only (there is no phone to
 * fall back to once on email). BR-034: the copy says "verification code", never
 * "passcode" (the Figma "passcode" string is normalized in the locale).
 *
 * @param {object} props
 * @param {(screen: string) => void} [props.goTo] - parent auth-flow navigator.
 * @param {Function} [props.completeAuthentication] - SIM-10 terminal handler.
 * @param {Function} [props.onBusyChange] - reports the wrapper's in-flight state up
 *   to SignIn.f.jsx so the shared dark-scrim Loader shows while the wrapper-level
 *   initial send is in flight (instead of a blank slot). Forwarded to OtpEntryScreen
 *   too, so the loader also covers verify/resend/redirect.
 * @param {object} props.authContext - carried forward from PasswordFallbackScreen
 *   via useAuthFlow.authContext (ADR-SIM8-001). Fields consumed: mfaMode,
 *   securityToken, credentialType, enteredCredential, phoneLast4Digits,
 *   maskedDestination, maskedEmail, keepSignedIn.
 */
const RiskStepUpOtpScreen = ({ goTo = noop, completeAuthentication = noop, onBusyChange = noop, authContext }) => {
    // Active OTP channel. Starts from the forced-challenge mfaMode and can be
    // swapped phone -> email ONCE via the "No Access to this phone?" affordance
    // (Figma 2271-60328). Local state (not a re-navigation) keeps the swap
    // self-contained and preserves the ONE useOtpSession instance (BR-025 carry).
    const [activeMode, setActiveMode] = useState(() => (authContext?.mfaMode === MFA_MODE.PHONE ? MFA_MODE.PHONE : MFA_MODE.EMAIL));
    const method = activeMode === MFA_MODE.PHONE ? 'phone' : 'email';

    // The authContext handed to the child + the send. In phone mode it is the
    // forward-carried context verbatim; after an email swap the channel-specific
    // fields (mfaMode + masked destination) are overridden to the email channel,
    // reusing the maskedEmail already carried from the credential lookup (merged
    // through goTo, ADR-SIM8-001) — securityToken/credentialType are unchanged.
    const effectiveAuthContext = useMemo(() => {
        if (activeMode === MFA_MODE.EMAIL && authContext?.mfaMode !== MFA_MODE.EMAIL) {
            return { ...authContext, mfaMode: MFA_MODE.EMAIL, maskedDestination: authContext?.maskedEmail };
        }

        return authContext;
    }, [activeMode, authContext]);

    // ONE session per wrapper lifetime (P2-T7): counters + cooldown persist across
    // re-renders. Recreating this hook would double the initial /validateUser send.
    const session = useOtpSession();
    // Tracks whether the initial /validateUser send has resolved AND its
    // setOtpSession has landed — child mounts only once this is true, so the
    // masked-destination subtitle is present on OtpEntryScreen's first render
    // (no proxy/fallback needed, and the test assertion for callCount==1 sees
    // the completed POST because the render is gated on the resolution).
    const [sendSettled, setSendSettled] = useState(false);
    // Whether the wrapper's initial send SUCCEEDED. This drives skipInitialSend on
    // the child: on success the wrapper already sent (child skips → POST fires
    // EXACTLY ONCE, P2-T7); on FAILURE the child must run its OWN sendInitial so it
    // can surface the standard otpSendFailed copy + resend UX. Without this, a failed
    // wrapper send would mount the child with skipInitialSend={true} → no code sent,
    // an empty session (useOtpSession only seeds otpSession AFTER a successful
    // dispatch), and resend()'s securityToken/mfaMode guard makes retries no-ops —
    // a dead-end OTP screen (Copilot Critical).
    const [initialSendSucceeded, setInitialSendSucceeded] = useState(false);

    // Fire the initial /validateUser send once, on mount, from an effect (NOT during
    // render — side effects belong in useEffect). Empty deps = exactly ONE send per
    // wrapper lifetime. The shared dark-scrim Loader (owned by SignIn.f.jsx) is shown
    // while the send is in flight via onBusyChange, so the slot is never a blank
    // `null` frame (Copilot a11y/UX). Awaiting inside the effect flips sendSettled
    // after the setter lands, so the child mounts with the masked destination already
    // in session. A `mounted` guard prevents a late setState after unmount. On
    // rejection we record the failure so the child owns the send-failure UX (see
    // initialSendSucceeded).
    useEffect(() => {
        let mounted = true;

        onBusyChange(true);
        (async () => {
            try {
                await session.sendInitial(effectiveAuthContext);

                if (mounted) {
                    setInitialSendSucceeded(true);
                }
            } catch (error) {
                noop(error);
            } finally {
                if (mounted) {
                    setSendSettled(true);
                    onBusyChange(false);
                }
            }
        })();

        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Alt-channel swap (Figma 2271-60328): "No Access to this phone? Send
    // verification code via email". Fires an email OTP send on the SAME session
    // (counters/cooldown carry — BR-025) then flips activeMode so OtpEntryScreen
    // re-renders in email mode with the masked-email destination. Guarded so the
    // subtitle stays populated on the re-render: gate the mode flip on sendSettled
    // being re-armed after the email send lands. `sendCodeViaEmail` is a Link
    // (blue via color='link'), never a Button, per the design.
    const swapToEmail = useCallback(async () => {
        const emailContext = { ...authContext, mfaMode: MFA_MODE.EMAIL, maskedDestination: authContext?.maskedEmail };
        setSendSettled(false);
        setInitialSendSucceeded(false);
        onBusyChange(true);

        try {
            await session.sendInitial(emailContext);
            setInitialSendSucceeded(true);
        } catch (error) {
            noop(error);
        } finally {
            setActiveMode(MFA_MODE.EMAIL);
            setSendSettled(true);
            onBusyChange(false);
        }
    }, [authContext, session, onBusyChange]);

    const handleSwapToEmail = useCallback(
        event => {
            event?.preventDefault?.();
            // Swallow the async rejection: a failed email send is owned by the
            // session's own resend/limit UX once OtpEntryScreen re-renders.
            Promise.resolve(swapToEmail()).catch(noop);
        },
        [swapToEmail]
    );

    // The affordance renders in PHONE mode only — once on email there is no phone
    // to fall back to (Figma 2271-60328 shows it on the phone-verification screen).
    // Reuses the design-system Link (components/Link) for the blue action; the
    // prefix is plain black copy.
    const altChannelAffordance =
        method === 'phone' ? (
            <p css={styles.altChannel}>
                {getText('noAccessToPhonePrefix')}
                <Link
                    color='link'
                    underline={true}
                    onClick={handleSwapToEmail}
                >
                    {getText('sendCodeViaEmail')}
                </Link>
            </p>
        ) : null;

    // BR-041 forced-challenge whitelist: intercept the OtpEntryScreen's Try-Another
    // -Method navigation. OtpEntryScreen calls goTo('authorization') from a fixed
    // button — the wrapper allows THAT single target through to the method chooser
    // (a screen SignIn actually renders) and blocks every other bypass path.
    const constrainedGoTo = useCallback(
        target => {
            if (target === OTP_TRY_ANOTHER_TARGET) {
                goTo(CHANNEL_SWAP_TARGET);
            }
            // Any other target ('password', 'credential', etc.) is a no-op: the UI
            // must not surface an affordance for it, and even if a regression did,
            // it stops here.
        },
        [goTo]
    );

    if (!sendSettled) {
        // Render nothing while the initial send is in flight — but the shell shows
        // the shared dark-scrim Loader over this slot for the whole window (driven by
        // onBusyChange(true) in the mount/swap effects), so the customer sees a
        // progress indicator, not a blank frame. Gating the child mount on the
        // resolved send keeps the P2-T7 exact-count guarantee.
        return null;
    }

    return (
        <OtpEntryScreen
            goTo={constrainedGoTo}
            method={method}
            session={session}
            authContext={effectiveAuthContext}
            completeAuthentication={completeAuthentication}
            onBusyChange={onBusyChange}
            // Skip the child's own send ONLY when the wrapper's send succeeded (the
            // POST already fired — P2-T7). On a FAILED wrapper send, let the child run
            // its own sendInitial so it surfaces otpSendFailed + a working resend
            // instead of a dead-end screen (Copilot Critical).
            skipInitialSend={initialSendSucceeded}
            altChannelAffordance={altChannelAffordance}
        />
    );
};

const styles = {
    // "No Access to this phone? Send verification code via email" (Figma 2271-60328):
    // black prefix + blue Link, 14px body, sits below the "Try Another Method" pill.
    altChannel: {
        margin: 0,
        color: colors.black,
        fontSize: fontSizes.base
    }
};

export default wrapFunctionalComponent(RiskStepUpOtpScreen, 'RiskStepUpOtpScreen');
